import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSupabaseClient } from "../providers/supabase";
import { useHtClient } from "../providers/ht";

interface Session {
  access_token?: string;
  refresh_token?: string;
  user?: {
    id: string;
    email: string;
  };
}

interface Profile {
  id: string;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  firm: number | null;
  office: number | null;
  user_type: string;
  sf_account: string | null;
  email: string | null;
  info: Record<string, any> | null;
}

interface LoginResponse {
  session: Session;
  profile: Profile;
}

export const useAuthSession = () => {
  const { data: supabase, isLoading: isSupabaseLoading } = useSupabaseClient();

  return useQuery({
    queryKey: ["useAuthSession"],
    enabled: !!supabase,
    queryFn: async () => {
      if (!supabase) {
        console.log("🔴 AUTH: Supabase not initialized");
        throw new Error("Supabase not initialized");
      }

      console.log("🔍 AUTH: Checking session...");
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.error("❌ AUTH: Session error:", error);
        throw error;
      }

      console.log("📦 AUTH: Session state:", {
        hasSession: !!session,
        userId: session?.user?.id,
        accessToken: session?.access_token ? "present" : "missing",
        refreshToken: session?.refresh_token ? "present" : "missing",
        expiresAt: session?.expires_at,
      });

      return session;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
};

export const useSignInWithPassword = () => {
  const { data: supabase } = useSupabaseClient();
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }) => {
      console.log("🔑 AUTH: Attempting sign in:", { email });

      if (!supabase || !client) {
        console.log("❌ AUTH: Missing clients:", {
          hasSupabase: !!supabase,
          hasHtClient: !!client,
        });
        throw new Error("Clients not initialized");
      }

      try {
        // First sign in with Supabase
        console.log("🔄 AUTH: Signing in with Supabase...");
        const {
          data: { session },
          error: supabaseError,
        } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (supabaseError) {
          console.error("❌ AUTH: Supabase sign in error:", supabaseError);
          throw supabaseError;
        }

        console.log("✅ AUTH: Supabase sign in successful:", {
          hasSession: !!session,
          userId: session?.user?.id,
        });

        // Then get the profile from our backend
        console.log("🔄 AUTH: Fetching profile from backend...");
        const { data: loginData } = await client.post("/auth/login", {
          email,
          password,
        });

        console.log("✅ AUTH: Profile fetch successful:", {
          hasProfile: !!loginData?.profile,
          userType: loginData?.profile?.user_type,
        });

        // Invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: ["useAuthSession"] });
        queryClient.invalidateQueries({ queryKey: ["useUser"] });
        queryClient.invalidateQueries({ queryKey: ["useCurrentProfile"] });

        return loginData;
      } catch (error) {
        console.error("❌ AUTH: Sign in process failed:", error);
        throw error;
      }
    },
  });
};

export const useSignUpWithPassword = () => {
  const { data: client } = useHtClient();
  const { data: supabase } = useSupabaseClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      password,
      is_client,
      first_name,
      last_name,
      phone,
      sf_account,
    }: {
      email: string;
      password: string;
      is_client: boolean;
      first_name: string;
      last_name: string;
      phone: string | undefined;
      sf_account: string | undefined;
    }) => {
      console.log("🔐 SIGNUP: Starting signup process for", email);

      if (!client || !supabase) {
        console.error("❌ SIGNUP: Clients not initialized", {
          hasClient: !!client,
          hasSupabase: !!supabase,
        });
        throw new Error("Clients not initialized");
      }

      const startTime = performance.now();
      const response = await client.post("/auth/signup", {
        email,
        password,
        is_client,
        first_name,
        last_name,
        phone,
        sf_account,
      });
      console.log(
        `⏱️ SIGNUP: Request took ${Math.round(performance.now() - startTime)}ms`
      );

      // Extract session and profile from response
      const { session, profile } = response.data;
      
      // Set the session in Supabase client so it knows about the authenticated user
      console.log("🔄 SIGNUP: Setting session in Supabase client...");
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });

      if (sessionError) {
        console.error("❌ SIGNUP: Failed to set session:", sessionError);
        throw sessionError;
      }

      // Update the session in cache
      queryClient.setQueryData(["useAuthSession"], session);
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["useAuthSession"] });
      queryClient.invalidateQueries({ queryKey: ["useUser"] });
      queryClient.invalidateQueries({ queryKey: ["useCurrentProfile"] });

      console.log("✅ SIGNUP: Successfully created account and set session");
      return response.data;
    },
  });
};

export const useLogout = () => {
  const { data: supabase } = useSupabaseClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!supabase) throw new Error("Supabase not initialized");

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear relevant cache
      queryClient.setQueryData(["useAuthSession"], null);
      queryClient.clear();
    },
  });
};

export const useUser = () => {
  const { data: supabase } = useSupabaseClient();

  return useQuery({
    queryKey: ["useUser"],
    queryFn: async () => {
      if (!supabase) throw new Error("Supabase not initialized");

      const {
        data: { user },
        error,
      } = await supabase.auth.getUser();
      if (error) throw error;

      return user;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

export const useInviteUser = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      profile,
    }: {
      email: string;
      profile: {
        first_name: string;
        last_name: string;
        user_type: string;
        sf_account: string;
        email: string;
      };
    }) => {
      console.log("🔐 INVITE: Starting invite process for", email);

      if (!client) {
        console.error("❌ INVITE: API client not initialized");
        throw new Error("API client not initialized");
      }

      const startTime = performance.now();
      const response = await client.post("/auth/invite-user", {
        email,
        profile,
      });
      console.log(
        `⏱️ INVITE: Request took ${Math.round(performance.now() - startTime)}ms`
      );

      console.log("✅ INVITE: Successfully invited user");
      return response.data;
    },
  });
};

export const useSetPassword = () => {
  const { data: client } = useHtClient();

  return useMutation({
    mutationFn: async ({
      email,
      password,
      token,
      access_token,
    }: {
      email: string;
      password: string;
      token?: string;
      access_token?: string;
    }) => {
      console.log("🔐 SET PASSWORD: Starting password set for", email);

      if (!client) {
        console.error("❌ SET PASSWORD: API client not initialized");
        throw new Error("API client not initialized");
      }

      const payload: any = {
        email,
        password,
      };
      
      if (token) {
        payload.token = token;
      }
      // The invite/recovery link's session token proves the person controls this
      // account; the backend refuses a password change without it (2026-09-24).
      if (access_token) {
        payload.access_token = access_token;
      }

      const startTime = performance.now();
      
      try {
        const response = await client.post("/auth/set-password", payload);
        console.log(
          `⏱️ SET PASSWORD: Request took ${Math.round(
            performance.now() - startTime
          )}ms`
        );

        console.log("✅ SET PASSWORD: Successfully set password");
        return response.data;
      } catch (error: any) {
        console.error("❌ SET PASSWORD: API error:", error);
        
        // Handle different types of errors
        if (error.response?.status === 400) {
          const detail = error.response?.data?.detail || error.message;
          if (detail.includes("Invalid or expired reset token")) {
            throw new Error("Invalid or expired reset link. Please request a new password reset.");
          } else if (detail.includes("Password must be at least")) {
            throw new Error(detail);
          } else {
            throw new Error(detail);
          }
        } else if (error.response?.status === 404) {
          throw new Error("User not found. Please check your email address.");
        } else {
          throw new Error("Failed to set password. Please try again later.");
        }
      }
    },
  });
};

export const useForgotPassword = () => {
  const { data: client } = useHtClient();

  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      console.log(
        "🔐 FORGOT PASSWORD: Starting password reset request for",
        email
      );

      if (!client) {
        console.error("❌ FORGOT PASSWORD: API client not initialized");
        throw new Error("API client not initialized");
      }

      console.log(
        "🔐 FORGOT PASSWORD: Making API call to /auth/forgot-password"
      );
      const startTime = performance.now();
      
      try {
        const response = await client.post("/auth/forgot-password", {
          email,
        });
        console.log(
          `⏱️ FORGOT PASSWORD: Request took ${Math.round(
            performance.now() - startTime
          )}ms`
        );
        console.log("🔐 FORGOT PASSWORD: API response:", response.data);

        console.log("✅ FORGOT PASSWORD: Successfully sent reset email");
        return response.data;
      } catch (error: any) {
        console.error("❌ FORGOT PASSWORD: API error:", error);
        
        // Handle different types of errors
        if (error.response?.status === 429) {
          throw new Error("Too many password reset requests. Please wait a minute before trying again.");
        } else if (error.response?.status === 400) {
          const detail = error.response?.data?.detail || error.message;
          throw new Error(detail);
        } else {
          throw new Error("Failed to send reset email. Please try again later.");
        }
      }
    },
  });
};
